/**
 * 送信元アカウントのテスト送信ダイアログ（Phase 26）。
 * 初期値は呼び出しユーザーの Firebase Auth email、自由に変更可能。
 */
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useTestMailAccount,
  type MailAccountRecord,
} from '@/hooks/useMailAccounts';

interface Props {
  account: MailAccountRecord | null;
  onOpenChange: (open: boolean) => void;
}

export function TestSendDialog({ account, onOpenChange }: Props) {
  const { user } = useAuth();
  const initialEmail = user?.email ?? '';
  const [toEmail, setToEmail] = useState(initialEmail);
  const testMutation = useTestMailAccount();

  useEffect(() => {
    if (account) setToEmail(initialEmail);
  }, [account, initialEmail]);

  const handleSubmit = async () => {
    if (!account) return;
    if (!toEmail.trim()) {
      toast.error('送信先を入力してください');
      return;
    }
    try {
      const res = await testMutation.mutateAsync({
        accountId: account.id,
        toEmail: toEmail.trim(),
      });
      toast.success(`テストメールを送信しました（${res.provider}）`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open={Boolean(account)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>テスト送信</DialogTitle>
          <DialogDescription>
            選択した送信元から指定アドレスにテストメールを送信します
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 text-xs">
            <div className="text-[var(--text-muted)]">送信元</div>
            <div className="font-mono">{account?.email}</div>
          </div>
          <div>
            <Label className="text-xs">送信先（テスト宛先）</Label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="test@example.com"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={testMutation.isPending}
          >
            {testMutation.isPending && <Spinner className="size-3 mr-1" />}
            テスト送信
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
